import {
    StyleProp,
    ImageStyle,
    Image,
    ImageResizeMode,
    Pressable,
    View,
    GestureResponderEvent,
} from "react-native";
import * as React from "react";
import CachedImage from "../../components/CachedImage";
import { useMemo, useState, useRef, useCallback } from "react";
import { enlargeAvatarData } from "../../atoms/avatar";
import { useRecoilValue, useSetRecoilState } from "recoil";

type UserAvatarProps = {
    avatarPath?: string | number | null;
    size?: number;
    style?: StyleProp<ImageStyle>;
    testID?: string;
    canEnlarge?: boolean;
};

const UserAvatar: React.FC<UserAvatarProps> = ({
    avatarPath,
    size = 55,
    style,
    testID,
    canEnlarge = true,
}: UserAvatarProps) => {
    const enlargeAvatarAtom = useRecoilValue(enlargeAvatarData);
    const setEnlargeAvatarAtom = useSetRecoilState(enlargeAvatarData);

    const [isVisible, setIsVisible] = useState(true);
    const baseStyle = useMemo<StyleProp<ImageStyle>>(
        () => ({
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: "#eee",
            overflow: "hidden" as const,
            opacity: +isVisible,
        }),
        [isVisible, size],
    );

    const imageProps = useMemo(
        () => ({
            testID,
            style: [baseStyle, style],
            resizeMode: "cover" as ImageResizeMode,
        }),
        [baseStyle, style, testID],
    );

    const ref = useRef<View>(null);

    React.useEffect(() => {
        if (!enlargeAvatarAtom) {
            setIsVisible(true);
        }
    }, [enlargeAvatarAtom]);

    const onPressAvatar = useCallback(
        (e: GestureResponderEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (avatarPath) {
                ref.current?.measureInWindow((x, y) => {
                    setEnlargeAvatarAtom({
                        image: {
                            avatarPath,
                            props: imageProps,
                        },
                        initialPosition: {
                            pageX: x,
                            pageY: y,
                            size,
                        },
                    });
                    setIsVisible(false);
                });
            }
        },
        [avatarPath, imageProps, setEnlargeAvatarAtom, size],
    );

    if (!avatarPath) {
        return null;
    }

    let content;

    if (typeof avatarPath === "number") {
        // number is used for require()d images
        content = <Image source={avatarPath} {...imageProps} />;
    } else {
        content = <CachedImage source={avatarPath} {...imageProps} />;
    }

    if (!canEnlarge) {
        return content;
    }

    return (
        <Pressable ref={ref} onPress={onPressAvatar}>
            {content}
        </Pressable>
    );
};

export default UserAvatar;
